import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./TestingPageFour.css";

const PLANET_TEXTURE =
  "https://cdn.jsdelivr.net/gh/mrdoob/three.js@r162/examples/textures/planets/earth_atmos_2048.jpg";

const LAYERS = [
  {
    id: "intro",
    type: "text",
    eyebrow: "Hermes",
    title: "Gift Me A Dream",
    subtitle: "Winter campaign study",
    description:
      "The reel drifts like paper in a light breeze. Each layer sticks at the left edge long enough for the next to glide over it, keeping the scroll fluid instead of snapping like a slide deck.",
    meta: [
      { label: "Role", value: "Illustration" },
      { label: "Client", value: "Hermes" },
      { label: "Motion", value: "Layered drift" },
      { label: "Format", value: "Horizontal reel" },
    ],
    accent: "#d7a06a",
    paper: "#f7efe5",
  },
  {
    id: "image-1",
    type: "image",
    title: "Paper Flight",
    caption: "Soft matte paper and gentle shadows.",
    image:
      "https://images.unsplash.com/photo-1489515217757-5fd1be406fef?auto=format&fit=crop&w=1600&q=80",
    accent: "#dcc2a8",
    paper: "#f4ede4",
  },
  {
    id: "text-1",
    type: "text",
    eyebrow: "Layer 02",
    title: "Slow Cover",
    subtitle: "Sticky left edge",
    description:
      "As each panel reaches the left edge, it holds for a beat. The next layer catches up and covers it, giving the handoff a slow, tactile rhythm.",
    meta: [
      { label: "Behavior", value: "Sticky overlap" },
      { label: "Pace", value: "Breathing" },
      { label: "Input", value: "Wheel + drag" },
      { label: "Control", value: "Free stop" },
    ],
    accent: "#8ea6b5",
    paper: "#eff3f6",
  },
  {
    id: "image-2",
    type: "image",
    title: "Cloud Drift",
    caption: "Muted gradients for the mid reel.",
    image:
      "https://images.unsplash.com/photo-1446694292248-2c2a7e575b1c?auto=format&fit=crop&w=1600&q=80",
    accent: "#b9c7d2",
    paper: "#eef2f5",
  },
  {
    id: "text-2",
    type: "text",
    eyebrow: "Layer 03",
    title: "Continuous Scroll",
    subtitle: "No snap points",
    description:
      "The reel never forces a snap. You can stop anywhere, then keep drifting until the next layer takes over the left edge.",
    meta: [
      { label: "Scroll", value: "Continuous" },
      { label: "Stops", value: "Anywhere" },
      { label: "Hand-off", value: "Cover" },
      { label: "Feel", value: "Paper" },
    ],
    accent: "#c69b7a",
    paper: "#f6ede4",
  },
  {
    id: "image-3",
    type: "image",
    title: "Night Field",
    caption: "Cooler tones to close the reel.",
    image:
      "https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1600&q=80",
    accent: "#8c97b2",
    paper: "#edf0f6",
  },
];

const clamp = (min, value, max) => Math.min(max, Math.max(min, value));
const MOMENTUM_DECAY = 0.86;
const MOMENTUM_MIN = 0.02;
const MOMENTUM_MAX = 3.5;
const WHEEL_MOMENTUM = 0.012;

export default function TestingPageFour() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const scrollRef = useRef(null);
  const dragState = useRef({
    isDown: false,
    startX: 0,
    scrollLeft: 0,
    lastTime: 0,
    lastScroll: 0,
    velocity: 0,
  });
  const momentumRef = useRef({
    velocity: 0,
    lastTime: 0,
    frame: null,
    active: false,
  });
  const layerMetrics = useRef([]);

  const getSlowFactor = useCallback((base, rawTarget) => {
    const metrics = layerMetrics.current;
    if (!metrics.length) return 1;

    const direction = Math.sign(rawTarget - base);
    if (direction === 0) return 1;
    if (direction < 0) return 1;

    let candidate = null;
    for (let i = 0; i < metrics.length; i += 1) {
      if (metrics[i].offset <= base + 1) {
        candidate = metrics[i];
      } else {
        break;
      }
    }
    if (!candidate) return 1;

    const distancePastLeft = Math.max(0, base - candidate.offset);
    const delay = candidate.width * 0.08;
    if (distancePastLeft <= delay) return 1;

    const slowZone = candidate.width * 0.9;
    if (distancePastLeft < slowZone) {
      const progress = (distancePastLeft - delay) / (slowZone - delay);
      const eased = Math.pow(progress, 1.8);
      const minFactor = 0.06;
      return minFactor + (1 - minFactor) * eased;
    }

    return 1;
  }, []);

  const measureLayers = useCallback(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const layers = scroller.querySelectorAll("[data-layer]");
    layerMetrics.current = Array.from(layers).map((layer) => ({
      offset: layer.offsetLeft,
      width: layer.offsetWidth,
    }));
  }, []);

  useEffect(() => {
    measureLayers();
    window.addEventListener("resize", measureLayers);
    return () => window.removeEventListener("resize", measureLayers);
  }, [measureLayers]);

  useEffect(() => {
    if (!isOpen) return;
    const scroller = scrollRef.current;
    if (!scroller) return;
    scroller.scrollLeft = 0;
    const frame = requestAnimationFrame(measureLayers);
    return () => cancelAnimationFrame(frame);
  }, [isOpen, measureLayers]);

  useEffect(() => {
    if (!isOpen) return;
    const { style } = document.body;
    const previousOverflow = style.overflow;
    style.overflow = "hidden";
    return () => {
      style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const stopMomentum = useCallback(() => {
    const momentum = momentumRef.current;
    if (momentum.frame) {
      cancelAnimationFrame(momentum.frame);
    }
    momentum.frame = null;
    momentum.active = false;
    momentum.velocity = 0;
  }, []);

  const startMomentum = useCallback(() => {
    const momentum = momentumRef.current;
    if (momentum.active) return;
    momentum.active = true;
    momentum.lastTime = performance.now();

    const step = (time) => {
      const scroller = scrollRef.current;
      if (!scroller) {
        stopMomentum();
        return;
      }

      const dt = Math.min(32, Math.max(1, time - momentum.lastTime || 16));
      momentum.lastTime = time;

      const base = scroller.scrollLeft;
      const rawTarget = base + momentum.velocity * dt;
      const factor = getSlowFactor(base, rawTarget);
      const adjustedTarget = base + (rawTarget - base) * factor;
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      const clampedTarget = clamp(0, adjustedTarget, maxScroll);
      scroller.scrollLeft = clampedTarget;

      if (clampedTarget === 0 || clampedTarget === maxScroll) {
        momentum.velocity = 0;
      } else {
        momentum.velocity *= Math.pow(MOMENTUM_DECAY, dt / 16);
      }

      if (Math.abs(momentum.velocity) < MOMENTUM_MIN) {
        stopMomentum();
        return;
      }

      momentum.frame = requestAnimationFrame(step);
    };

    momentum.frame = requestAnimationFrame(step);
  }, [getSlowFactor, stopMomentum]);

  useEffect(() => {
    if (!isOpen) {
      stopMomentum();
    }
  }, [isOpen, stopMomentum]);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return undefined;

    const handleWheel = (event) => {
      if (!isOpen) return;
      const delta =
        Math.abs(event.deltaY) > Math.abs(event.deltaX)
          ? event.deltaY
          : event.deltaX;
      if (delta === 0) return;

      event.preventDefault();

      const base = scroller.scrollLeft;
      const rawTarget = base + delta;
      const factor = getSlowFactor(base, rawTarget);
      const adjustedDelta = (rawTarget - base) * factor;

      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      scroller.scrollLeft = clamp(0, base + adjustedDelta, maxScroll);

      const momentum = momentumRef.current;
      momentum.velocity = clamp(
        -MOMENTUM_MAX,
        momentum.velocity + adjustedDelta * WHEEL_MOMENTUM,
        MOMENTUM_MAX,
      );
      if (Math.abs(momentum.velocity) > MOMENTUM_MIN) {
        startMomentum();
      }
    };

    scroller.addEventListener("wheel", handleWheel, { passive: false });
    return () => scroller.removeEventListener("wheel", handleWheel);
  }, [getSlowFactor, isOpen, startMomentum]);

  const handlePlanetClick = () => {
    if (isOpen) return;
    setIsOpen(true);
  };

  const handleClose = () => {
    if (!isOpen) return;
    setIsOpen(false);
  };

  const handlePointerDown = (event) => {
    if (!isOpen) return;
    const scroller = scrollRef.current;
    if (!scroller) return;
    stopMomentum();
    dragState.current.isDown = true;
    dragState.current.startX = event.clientX;
    dragState.current.scrollLeft = scroller.scrollLeft;
    dragState.current.lastTime = performance.now();
    dragState.current.lastScroll = scroller.scrollLeft;
    dragState.current.velocity = 0;
    scroller.setPointerCapture(event.pointerId);
    setIsDragging(true);
  };

  const handlePointerMove = (event) => {
    if (!dragState.current.isDown) return;
    const scroller = scrollRef.current;
    if (!scroller) return;
    const delta = event.clientX - dragState.current.startX;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const base = scroller.scrollLeft;
    const rawTarget = dragState.current.scrollLeft - delta;
    const factor = getSlowFactor(base, rawTarget);
    const adjustedTarget = base + (rawTarget - base) * factor;
    scroller.scrollLeft = clamp(0, adjustedTarget, maxScroll);

    const now = performance.now();
    const dt = Math.max(1, now - dragState.current.lastTime);
    dragState.current.velocity =
      (scroller.scrollLeft - dragState.current.lastScroll) / dt;
    dragState.current.lastTime = now;
    dragState.current.lastScroll = scroller.scrollLeft;
  };

  const endPointerDrag = (event) => {
    if (!dragState.current.isDown) return;
    dragState.current.isDown = false;
    setIsDragging(false);
    const { velocity } = dragState.current;
    if (Math.abs(velocity) > MOMENTUM_MIN) {
      const momentum = momentumRef.current;
      momentum.velocity = clamp(-MOMENTUM_MAX, velocity, MOMENTUM_MAX);
      startMomentum();
    }
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch (error) {
      // Ignore release failures for non-captured pointers.
    }
  };

  return (
    <div className={`testing4-page ${isOpen ? "is-open" : ""}`}>
      <Link to="/" className="testing4-back">
        Back
      </Link>

      <main className="testing4-stage">
        <div className="testing4-planet-stage">
          <button
            type="button"
            className="testing4-planet"
            onClick={handlePlanetClick}
            disabled={isOpen}
            aria-label="Open the horizontal reel"
          >
            <span className="testing4-planet__glow" aria-hidden="true" />
            <span
              className="testing4-planet__surface"
              style={{ backgroundImage: `url(${PLANET_TEXTURE})` }}
              aria-hidden="true"
            />
            <span className="testing4-planet__shine" aria-hidden="true" />
          </button>
        </div>

        <aside className="testing4-reel" aria-hidden={!isOpen}>
          <div className="testing4-reel__header">
            <span className="testing4-reel__eyebrow">Testing 4</span>
            <button
              type="button"
              className="testing4-reel__close"
              onClick={handleClose}
            >
              Close
            </button>
          </div>

          <div
            className={`testing4-reel__scroller ${
              isDragging ? "is-dragging" : ""
            }`}
            ref={scrollRef}
            role="region"
            aria-label="Horizontal reel"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endPointerDrag}
            onPointerLeave={endPointerDrag}
            onPointerCancel={endPointerDrag}
          >
            <div className="testing4-reel__track">
              <div className="testing4-reel__spacer" aria-hidden="true" />
              {LAYERS.map((layer, index) => (
                <article
                  key={layer.id}
                  data-layer
                  className={`testing4-layer testing4-layer--${layer.type}`}
                  style={{
                    "--layer-index": index + 1,
                    "--layer-accent": layer.accent,
                    "--layer-paper": layer.paper,
                  }}
                >
                  {layer.type === "text" ? (
                    <div className="testing4-layer__text">
                      <span className="testing4-layer__eyebrow">
                        {layer.eyebrow}
                      </span>
                      <h2 className="testing4-layer__title">{layer.title}</h2>
                      <p className="testing4-layer__subtitle">
                        {layer.subtitle}
                      </p>
                      <p className="testing4-layer__description">
                        {layer.description}
                      </p>
                      <div className="testing4-layer__meta">
                        {layer.meta.map((item) => (
                          <div key={item.label}>
                            <span>{item.label}</span>
                            <strong>{item.value}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <figure className="testing4-layer__image">
                      <img src={layer.image} alt={layer.title} loading="lazy" />
                      <figcaption>
                        <span>{layer.title}</span>
                        <span>{layer.caption}</span>
                      </figcaption>
                    </figure>
                  )}
                </article>
              ))}
            </div>
          </div>

          <div className="testing4-reel__indicator">
            Scroll to see more -&gt;
          </div>
        </aside>
      </main>
    </div>
  );
}
